/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import * as ProposalNS from '../../Proposal';

import * as ProposalElementNS from '../../ProposalElements';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { ComparisonItemDAO } from '../ProposalItemDAO';

// Components
import { NotApplicableCell, HoldingCell, PercentageCell } from '../../../../views/components/cells';

// Utils
import { ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import DonutData from '@/types/donut-data';
import LocalizedString from '@/types/localized-string';

export default class SectorAllocationDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new SectorAllocationDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['DEFAULT'];
  }

  // eslint-disable-next-line class-methods-use-this
  private hasEmptySectors(itemDAO: ComparisonItemDAO) {
    const equityAllocation = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.EQUITY).response;
    return equityAllocation > 0 && itemDAO.item.allocations && itemDAO.item.allocations.sector && itemDAO.item.allocations.sector.length === 0;
  }

  protected build(): void {
    let filterCount: number = 10;
    let displaySectorNames: boolean = false;
    let start: number = 1;
    let omit: string[] = [];

    // test for options
    const daoConfig: ProposalNS.SectorAllocationConfig = this.proposalElement.DAO as ProposalNS.SectorAllocationConfig;
    if (daoConfig.filterTopXDAOOptions !== undefined) {
      filterCount = daoConfig.filterTopXDAOOptions.count;

      if (daoConfig.filterTopXDAOOptions.start) {
        start = daoConfig.filterTopXDAOOptions.start;
      }

      omit = daoConfig.filterTopXDAOOptions.omit || [];
    }

    if (daoConfig.extraColumnsDAOOptions !== undefined && daoConfig.extraColumnsDAOOptions.columns.indexOf('SECTOR_NAME') !== -1) {
      displaySectorNames = true;
    }

    if (this.customFieldNames != null) {
      for (const name of this.customFieldNames.names) {
        this.addFieldName(this.formatLocalizedString(name), ProposalStyleClassNames.ProposalTableContent);
      }
    }

    const { locale } = this.proposal.options;
    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      let sectorKeys: LocalizedString[] = DataPoints.getItemSectorAllocationsKeys(itemDAO.item);

      if (omit.length) {
        sectorKeys = sectorKeys.filter(key => !omit.some(val => (key['en-CA'] ? key['en-CA'] === val : false)));
      }

      if (daoConfig.displayOther && this.hasEmptySectors(itemDAO)) {
        const fieldName = `${this.formatMessage('Common.Other')}*`;
        const field = new ProposalFieldDAO(fieldName, 100);
        field.cellType = HoldingCell;
        this.addFieldDAO(itemDAO, field);
        this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);

        if (displaySectorNames) {
          this.addFieldDAO(itemDAO, new ProposalFieldDAO(fieldName, ''), 1);
        }

        this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableContent);
      }

      const localItemDAO: ComparisonItemDAO = itemDAO;
      for (let i = start; i < start + filterCount; i++) {
        const sector = sectorKeys[i - 1];

        if (sector !== undefined && sector['en-CA']) {
          const dpResponse: DataPointResponse<number> = DataPoints.getItemSectorAllocationByKey(localItemDAO.item, sector['en-CA']);
          this.addDisclaimerKeys(dpResponse.disclaimerKeys, localItemDAO.item);
          let fieldName: string | undefined = sector[locale] || sector['en-CA'];

          if (sector['en-CA'] === 'Non Classified Equity' || sector['en-CA'] === 'Not Classified - Non Equity') {
            this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.NON_CLASSIFIED_EQUITY], localItemDAO.item);
            fieldName += '*';
          }

          let value: any = dpResponse.response.toFixed(2);
          let celltype: any = PercentageCell;
          if (displaySectorNames) {
            value = { weight: dpResponse.response.toFixed(2), name: fieldName };
            celltype = HoldingCell;
          }

          this.addItemField(localItemDAO, fieldName !== undefined ? fieldName : '', value, celltype);

          if (this.customFieldNames === null) {
            this.addFieldName(fieldName || '', ProposalStyleClassNames.ProposalTableContent);
          }
        } else {
          this.addItemField(itemDAO, '', {}, NotApplicableCell);
          if (this.customFieldNames === null) {
            this.addFieldName('', ProposalStyleClassNames.ProposalTableContent);
          }
        }
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }
    return out;
  }

  // private methods

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: any, celltype: any): number {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = celltype;
    this.addFieldDAO(itemDAO, field);
    return value;
  }
}
